<script setup>
import { computed } from 'vue';
import T from './CvPrintView.i18n.js';
import SvgIcon from 'JIX/components/SvgIcon.vue';
import DsSectionDivider from 'JIX/components/ds/DsSectionDivider.vue';
import UserProfileInfoPrint from 'JIX/components/user/ProfileInfoPrint.vue';
import CvPrintLanguageSkills from './view/CvPrintLanguageSkills.vue';
import CvPrintHistoryEntry from './view/CvPrintHistoryEntry.vue';

import 'icons/job-experience.svg';
import 'icons/manager-experience.svg';
import 'icons/education.svg';

const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
    cv: {
        type: Object,
        required: true,
    },
    languageLevels: {
        type: Array,
        required: true,
    },
    educationOptions: {
        type: Array,
        required: true,
    },
});

const validExperience = (val, fun) => val && val !== '0' ? fun(val) : null;
const yearsJobExperience = computed(() => validExperience(props.cv.jobexperiences, T.yearsJobExperience));
const yearsMgrExperience = computed(() => validExperience(props.cv.mgrexperiences, T.yearsMgrExperience));
const educationEntries = computed(() => {
    return props.cv.educationHistory.filter(e => e.type === 'education');
});
const courseEntries = computed(() => {
    return props.cv.educationHistory.filter(e => e.type === 'course');
});
const educationLevelText = computed(() => {
    if (props.cv.educationLevel === null || props.cv.educationLevel === undefined || props.cv.educationLevel === -1) {
        return '';
    }
    const option = props.educationOptions.find(([_text, value]) => value === props.cv.educationLevel);
    return option ? option[0] : '';
});
const hasOverviewInfo = computed(() => {
    return yearsJobExperience.value
        || yearsMgrExperience.value
        || educationLevelText.value
        || props.cv.languages.length
        || props.cv.keywords.length
        || props.cv.personalInfoHtml;
});

</script>

<template>
    <div class="mb-6">
        <UserProfileInfoPrint :user="user" class="row" />
        <div class="row">
            <div class="col-4 pr-4 pl-0 d-flex flex-column gap-5" v-if="hasOverviewInfo">
                <div v-if="yearsJobExperience || yearsMgrExperience || educationLevelText">
                    <DsSectionDivider
                        style="break-inside: avoid-page;"
                        :header="T.overview()"
                        :header-level="2"
                    />
                    <div class="d-flex flex-column gap-3 mt-2" style="break-before: avoid-page">
                        <div class="d-flex align-items-center text-xs" v-if="yearsJobExperience">
                            <SvgIcon
                                size="xs"
                                class="mr-1"
                                name="job-experience"
                                role="presentation"
                            />
                            {{ yearsJobExperience }}
                        </div>
                        <div class="d-flex align-items-center text-xs" v-if="yearsMgrExperience">
                            <SvgIcon
                                size="xs"
                                class="mr-1"
                                name="manager-experience"
                                role="presentation"
                            />
                            {{ yearsMgrExperience }}
                        </div>
                        <div class="d-flex align-items-center text-xs" v-if="educationLevelText">
                            <SvgIcon
                                size="xs"
                                class="mr-1"
                                name="education"
                                role="presentation"
                            />
                            {{ educationLevelText }}
                        </div>
                    </div>
                </div>
                <div v-if="cv.languages.length">
                    <DsSectionDivider
                        style="break-inside: avoid-page;"
                        :header="T.language()"
                        :header-level="2"
                    />
                    <div class="mt-2" style="break-before: avoid-page;">
                        <CvPrintLanguageSkills :languages="cv.languages" :levels="languageLevels" />
                    </div>
                </div>
                <div v-if="cv.keywords.length">
                    <DsSectionDivider
                        style="break-inside: avoid-page;"
                        :header="T.skills()"
                        :header-level="2"
                    />
                    <div class="mt-2" style="break-before: avoid-page;">
                        <div class="badge-list">
                            <span
                                v-for="keyword in cv.keywords"
                                :key="keyword.id"
                                class="badge badge-secondary badge-wrap"
                                style="print-color-adjust: exact"
                            >
                                {{ keyword.name }}
                            </span>
                        </div>
                    </div>
                </div>
                <div v-if="cv.personalInfoHtml">
                    <DsSectionDivider
                        style="break-inside: avoid-page;"
                        :header="T.aboutMe()"
                        :header-level="2"
                    />
                    <div class="mt-2" style="break-before: avoid-page;" v-html="cv.personalInfoHtml" />
                </div>
            </div>
            <div :class="[hasOverviewInfo ? 'col-8 border-left pl-4 pr-0' : 'col-12 px-0']">
                <div class="mb-5" v-if="cv.headline || cv.resumeHtml">
                    <h1 class="h2 overflow-hidden" style="overflow-wrap: anywhere;" v-if="cv.headline">
                        {{ cv.headline }}
                    </h1>
                    <div v-html="cv.resumeHtml" />
                </div>
                <div v-if="cv.jobHistory.length">
                    <DsSectionDivider
                        style="break-inside: avoid-page;"
                        :header="T.jobExperience()"
                        :header-level="2"
                    />
                    <div
                        class="pt-3"
                        :class="{ 'mb-5': educationEntries.length || courseEntries.length }"
                        style="break-before: avoid-page;"
                        role="list"
                    >
                        <CvPrintHistoryEntry
                            v-for="(entry, index) in cv.jobHistory"
                            :key="'job'+index"
                            :entry="entry"
                            is-job-experience
                        />
                    </div>
                </div>
                <div v-if="educationEntries.length">
                    <DsSectionDivider
                        style="break-inside: avoid-page;"
                        :header="T.education()"
                        :header-level="2"
                    />
                    <div
                        class="pt-3"
                        :class="{ 'mb-5': courseEntries.length }"
                        style="break-before: avoid-page;"
                        role="list"
                    >
                        <CvPrintHistoryEntry
                            v-for="(entry, index) in educationEntries"
                            :key="'edu'+index"
                            :entry="entry"
                        />
                    </div>
                </div>
                <div v-if="courseEntries.length">
                    <DsSectionDivider
                        style="break-inside: avoid-page;"
                        :header="T.courses()"
                        :header-level="2"
                    />
                    <div class="pt-3" style="break-before: avoid-page;" role="list">
                        <CvPrintHistoryEntry
                            v-for="(entry, index) in courseEntries"
                            :key="'course'+index"
                            :entry="entry"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
